import React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { Spinner } from '../Spinner/Spinner';
import type { LocalMarketCompetitor } from '../../growth-local-market/types';
import { DEFAULT_GOOGLE_MAPS_API_KEY } from '../../growth-local-market/config';

const GOOGLE_MAPS_SCRIPT_ID = 'google-maps-js';
const MAX_COMPETITORS = 60;
const MAX_SEARCH_RADIUS_MILES = 20;
const MILES_TO_METERS = 1609.34;
const GOOGLE_PLACES_PAGE_SIZE = 20;
const NEXT_PAGE_TOKEN_DELAY_MS = 1500;
const MAP_CAMERA_ANIMATION_BASE_MS = 900;
const MAP_CAMERA_ANIMATION_PER_ZOOM_LEVEL_MS = 180;
const MAP_CAMERA_ANIMATION_MAX_MS = 1500;

const MOCK_CENTER = { lat: 40.758, lng: -73.9855 };
const MOCK_CLINIC_NAME = 'Smile Dental, Midtown';

type Coordinate = { lat: number; lng: number };

type MapThemeColors = {
  competitor: string;
  surface: string;
  success: string;
  successTint: string;
};

type CompetitorMarkerEvent = 'click' | 'mouseover' | 'mouseout';

type CompetitorMarker = {
  addListener: (eventName: CompetitorMarkerEvent, handler: () => void) => void;
  setMap: (map: unknown) => void;
};

export interface GoogleLocalMarketMapProps {
  analysisRadiusMiles: number;
  clinicAddress?: string;
  clinicName: string;
  centerCoordinate?: { lat: number; lng: number } | null;
  competitors?: LocalMarketCompetitor[];
  googleMapsApiKey?: string;
  onCompetitorsChange?: (competitors: LocalMarketCompetitor[]) => void;
  onCompetitorViewMore?: (competitor: LocalMarketCompetitor) => void;
}

declare global {
  interface Window {
    google?: any;
    initGoogleMapsLocalMarket?: () => void;
  }
}

let googleMapsLoaderPromise: Promise<any> | null = null;

function loadGoogleMaps(apiKey: string = DEFAULT_GOOGLE_MAPS_API_KEY) {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Google Maps can only load in the browser.'));
  }

  if (window.google?.maps?.places) {
    return Promise.resolve(window.google);
  }

  if (googleMapsLoaderPromise) {
    return googleMapsLoaderPromise;
  }

  googleMapsLoaderPromise = new Promise((resolve, reject) => {
    const existingScript = document.getElementById(GOOGLE_MAPS_SCRIPT_ID);

    window.initGoogleMapsLocalMarket = () => {
      resolve(window.google);
    };

    if (existingScript) {
      existingScript.addEventListener('error', () => {
        reject(new Error('Failed to load Google Maps script.'));
      });
      return;
    }

    const script = document.createElement('script');
    script.id = GOOGLE_MAPS_SCRIPT_ID;
    script.async = true;
    script.defer = true;
    script.src =
      'https://maps.googleapis.com/maps/api/js' +
      `?key=${apiKey}` +
      '&libraries=places,geometry,marker' +
      '&loading=async' +
      '&callback=initGoogleMapsLocalMarket';
    script.addEventListener('error', () => {
      googleMapsLoaderPromise = null;
      reject(new Error('Failed to load Google Maps script.'));
    });
    document.head.appendChild(script);
  });

  return googleMapsLoaderPromise;
}

function getMapThemeColors(): MapThemeColors {
  if (typeof document === 'undefined') {
    return {
      competitor: '#C45B4E',
      surface: '#FFFFFF',
      success: '#2D7A5F',
      successTint: 'rgba(45, 122, 95, 0.25)',
    };
  }

  const style = getComputedStyle(document.documentElement);
  return {
    competitor: style.getPropertyValue('--terracotta').trim() || '#C45B4E',
    surface: style.getPropertyValue('--cream-1').trim() || '#FFFFFF',
    success: style.getPropertyValue('--success').trim() || '#2D7A5F',
    successTint: style.getPropertyValue('--success-tint').trim() || 'rgba(45, 122, 95, 0.25)',
  };
}

function buildClinicMarkerSvg(colors: MapThemeColors) {
  return encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" viewBox="0 0 60 60" fill="none">
  <circle cx="30" cy="30" r="30" fill="${colors.success}"/>
  <path d="M41 28.0455C41 36.6364 30 44 30 44C30 44 19 36.6364 19 28.0455C19 25.116 20.1589 22.3066 22.2218 20.2351C24.2847 18.1637 27.0826 17 30 17C32.9174 17 35.7153 18.1637 37.7782 20.2351C39.8411 22.3066 41 25.116 41 28.0455Z" stroke="${colors.surface}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M30 31.7273C32.025 31.7273 33.6667 30.0789 33.6667 28.0455C33.6667 26.012 32.025 24.3636 30 24.3636C27.975 24.3636 26.3333 26.012 26.3333 28.0455C26.3333 30.0789 27.975 31.7273 30 31.7273Z" stroke="${colors.surface}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
`);
}

function geocodeAddress(google: any, address: string): Promise<Coordinate> {
  return new Promise((resolve, reject) => {
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ address }, (results: any[], status: string) => {
      if (status !== 'OK' || !results?.[0]?.geometry?.location) {
        reject(new Error('Unable to find coordinates for the selected clinic.'));
        return;
      }

      const location = results[0].geometry.location;
      resolve({ lat: location.lat(), lng: location.lng() });
    });
  });
}

function getDistanceMiles(origin: Coordinate, destination: Coordinate) {
  const toRadians = (value: number) => (value * Math.PI) / 180;
  const earthRadiusMiles = 3958.8;
  const latitudeDelta = toRadians(destination.lat - origin.lat);
  const longitudeDelta = toRadians(destination.lng - origin.lng);
  const latitude1 = toRadians(origin.lat);
  const latitude2 = toRadians(destination.lat);
  const haversine =
    Math.sin(latitudeDelta / 2) ** 2 +
    Math.cos(latitude1) * Math.cos(latitude2) * Math.sin(longitudeDelta / 2) ** 2;

  return 2 * earthRadiusMiles * Math.asin(Math.sqrt(haversine));
}

function getDesiredCompetitorCount(radiusMiles: number) {
  return Math.min(
    MAX_COMPETITORS,
    Math.max(GOOGLE_PLACES_PAGE_SIZE, Math.ceil(radiusMiles / 5) * GOOGLE_PLACES_PAGE_SIZE),
  );
}

function filterCompetitorsByRadius(competitors: LocalMarketCompetitor[], radiusMiles: number) {
  return competitors
    .filter(
      (place) => place.coordinate != null && place.distanceMiles <= radiusMiles,
    )
    .sort((left, right) => left.distanceMiles - right.distanceMiles)
    .slice(0, getDesiredCompetitorCount(radiusMiles));
}

function searchNearbyClinics(
  google: any,
  center: Coordinate,
  radiusMiles: number,
  clinicName: string,
  map: any,
): Promise<LocalMarketCompetitor[]> {
  return new Promise((resolve, reject) => {
    const service = new google.maps.places.PlacesService(map);
    const desiredCompetitorCount = getDesiredCompetitorCount(radiusMiles);
    const seenPlaceIds = new Set<string>();
    const collectedResults: any[] = [];

    const finalizeResults = () => {
      const competitors: LocalMarketCompetitor[] = collectedResults
        .flatMap((place: any, index: number): LocalMarketCompetitor[] => {
          const location = place.geometry?.location;
          if (!location) return [];

          const coordinate = { lat: location.lat(), lng: location.lng() };
          const distanceMiles = getDistanceMiles(center, coordinate);
          const isInsideRadius = distanceMiles <= radiusMiles;
          const name = place.name || 'Nearby clinic';
          const isSameClinic = name.trim().toLowerCase() === clinicName.trim().toLowerCase();
          if (!isInsideRadius || isSameClinic) return [];

          return [
            {
              id: place.place_id || `${name}-${index}`,
              name,
              address: place.vicinity || place.formatted_address || '',
              distanceMiles,
              coordinate,
            },
          ];
        })
        .sort((left, right) => left.distanceMiles - right.distanceMiles)
        .slice(0, desiredCompetitorCount);

      resolve(competitors);
    };

    const collectPage = (
      pagination?: { hasNextPage?: boolean; nextPage?: () => void } | null,
    ) => {
      if (collectedResults.length >= desiredCompetitorCount || !pagination?.hasNextPage) {
        finalizeResults();
        return;
      }

      window.setTimeout(() => {
        pagination.nextPage?.();
      }, NEXT_PAGE_TOKEN_DELAY_MS);
    };

    service.nearbySearch(
      {
        location: center,
        radius: Math.round(radiusMiles * MILES_TO_METERS),
        keyword: 'dental clinic',
        type: 'dentist',
      },
      (
        results: any[],
        status: string,
        pagination?: { hasNextPage?: boolean; nextPage?: () => void },
      ) => {
        if (
          status !== google.maps.places.PlacesServiceStatus.OK &&
          status !== google.maps.places.PlacesServiceStatus.ZERO_RESULTS
        ) {
          reject(new Error('Unable to load nearby clinics from Google Places.'));
          return;
        }

        for (const place of results ?? []) {
          const placeId = place.place_id || `${place.name}-${collectedResults.length}`;
          if (seenPlaceIds.has(placeId)) continue;
          seenPlaceIds.add(placeId);
          collectedResults.push(place);
        }

        if (status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
          finalizeResults();
          return;
        }

        collectPage(pagination);
      },
    );
  });
}

function getZoomForRadius(radiusMiles: number) {
  if (radiusMiles <= 2) return 13;
  if (radiusMiles <= 5) return 12;
  if (radiusMiles <= 10) return 11;
  if (radiusMiles <= 15) return 10;
  return 9;
}

function easeInOutQuart(progress: number) {
  if (progress < 0.5) return 8 * progress * progress * progress * progress;
  return 1 - (-2 * progress + 2) ** 4 / 2;
}

function getCameraAnimationDuration(startZoom: number, targetZoom: number) {
  const zoomDelta = Math.abs(targetZoom - startZoom);
  return Math.min(
    MAP_CAMERA_ANIMATION_BASE_MS + zoomDelta * MAP_CAMERA_ANIMATION_PER_ZOOM_LEVEL_MS,
    MAP_CAMERA_ANIMATION_MAX_MS,
  );
}

function animateMapCamera(map: any, center: Coordinate, zoom: number) {
  if (typeof window === 'undefined') {
    map.setCenter(center);
    map.setZoom(zoom);
    return () => {};
  }

  const startCenter = map.getCenter?.();
  const startZoom = Number(map.getZoom?.() ?? zoom);

  if (!startCenter) {
    map.setCenter(center);
    map.setZoom(zoom);
    return () => {};
  }

  const initialLat = startCenter.lat();
  const initialLng = startCenter.lng();
  const duration = getCameraAnimationDuration(startZoom, zoom);
  let animationFrameId = 0;
  const animationStart = window.performance.now();

  const updateFrame = (timestamp: number) => {
    const rawProgress = Math.min((timestamp - animationStart) / duration, 1);
    const progress = easeInOutQuart(rawProgress);
    const nextCenter = {
      lat: initialLat + (center.lat - initialLat) * progress,
      lng: initialLng + (center.lng - initialLng) * progress,
    };
    const nextZoom = startZoom + (zoom - startZoom) * progress;

    if (typeof map.moveCamera === 'function') {
      map.moveCamera({ center: nextCenter, zoom: nextZoom });
    } else {
      map.setCenter(nextCenter);
      map.setZoom(nextZoom);
    }

    if (rawProgress < 1) {
      animationFrameId = window.requestAnimationFrame(updateFrame);
      return;
    }

    map.setCenter(center);
    map.setZoom(zoom);
  };

  animationFrameId = window.requestAnimationFrame(updateFrame);

  return () => {
    if (animationFrameId) window.cancelAnimationFrame(animationFrameId);
  };
}

function buildCompetitorInfoWindowContent(
  competitor: LocalMarketCompetitor,
  colors: MapThemeColors,
) {
  return renderToStaticMarkup(
    <div style={{ padding: 12, fontFamily: 'system-ui, sans-serif' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
        <p
          style={{
            margin: 0,
            maxWidth: 110,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontSize: 14,
            fontWeight: 600,
            color: 'var(--ink-2, #555)',
          }}
          title={competitor.name}
        >
          {competitor.name}
        </p>
        <span
          style={{
            borderRadius: 999,
            background: 'color-mix(in srgb, var(--terracotta, #C45B4E) 16%, transparent)',
            color: colors.competitor,
            fontSize: 12,
            fontWeight: 600,
            padding: '8px 10px',
          }}
        >
          Competitor
        </span>
      </div>
      <p style={{ margin: '12px 0 0', fontSize: 12, color: 'var(--ink-2, #555)' }}>
        Est. Ad Spend: <strong>$2k/mo</strong>
      </p>
      <p style={{ margin: '8px 0 0', fontSize: 12, color: 'var(--ink-2, #555)' }}>
        Top Keyword: <strong>Invisalign</strong>
      </p>
      <div style={{ marginTop: 12, borderTop: '1px solid var(--cream-2, #e5e5e5)' }} />
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          paddingTop: 12,
        }}
      >
        <button
          type="button"
          data-competitor-view-more={competitor.id}
          style={{
            background: 'transparent',
            border: 0,
            padding: 0,
            fontSize: 12,
            fontWeight: 600,
            color: colors.success,
            cursor: 'pointer',
          }}
        >
          View more
        </button>
        <span style={{ fontSize: 10, color: 'var(--ink-3, #888)' }}>
          {competitor.distanceMiles.toFixed(1)} miles away
        </span>
      </div>
    </div>,
  );
}

export function GoogleLocalMarketMap({
  analysisRadiusMiles,
  clinicAddress,
  clinicName,
  centerCoordinate = null,
  competitors: competitorsProp,
  googleMapsApiKey,
  onCompetitorsChange,
  onCompetitorViewMore,
}: GoogleLocalMarketMapProps) {
  const mapContainerRef = React.useRef<HTMLDivElement | null>(null);
  const googleRef = React.useRef<any>(null);
  const mapRef = React.useRef<any>(null);
  const mapCenterRef = React.useRef<Coordinate>(MOCK_CENTER);
  const clinicMarkerRef = React.useRef<any>(null);
  const circleRef = React.useRef<any>(null);
  const competitorMarkersRef = React.useRef<any[]>([]);
  const competitorsRef = React.useRef<LocalMarketCompetitor[]>([]);
  const allCompetitorsRef = React.useRef<LocalMarketCompetitor[]>([]);
  const infoWindowRef = React.useRef<any>(null);
  const activeMarkerRef = React.useRef<CompetitorMarker | null>(null);
  const stopCameraAnimationRef = React.useRef<(() => void) | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [mapLoadError, setMapLoadError] = React.useState<string | null>(null);
  const resolvedApiKey = googleMapsApiKey || DEFAULT_GOOGLE_MAPS_API_KEY;

  const syncVisibleCompetitors = React.useCallback(
    (radiusMiles: number) => {
      const google = googleRef.current;
      const map = mapRef.current;
      if (!google || !map) return;

      infoWindowRef.current?.close();
      activeMarkerRef.current = null;
      competitorMarkersRef.current.forEach((marker) => marker.setMap(null));
      competitorMarkersRef.current = [];

      const visibleCompetitors = filterCompetitorsByRadius(
        allCompetitorsRef.current,
        radiusMiles,
      );
      const colors = getMapThemeColors();

      competitorMarkersRef.current = visibleCompetitors
        .filter((competitor) => competitor.coordinate != null)
        .map((competitor) => {
        const marker: CompetitorMarker = new google.maps.Marker({
          map,
          position: competitor.coordinate!,
          title: competitor.name,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            fillColor: colors.competitor,
            fillOpacity: 1,
            strokeColor: colors.surface,
            strokeOpacity: 1,
            strokeWeight: 2,
            scale: 6,
          },
        });

        marker.addListener('mouseover', () => {
          activeMarkerRef.current = marker;
          infoWindowRef.current?.setContent(
            buildCompetitorInfoWindowContent(competitor, colors),
          );
          infoWindowRef.current?.open({ anchor: marker, map, shouldFocus: false });
        });

        return marker;
      });

      competitorsRef.current = visibleCompetitors;
      onCompetitorsChange?.(visibleCompetitors);
    },
    [onCompetitorsChange],
  );

  React.useEffect(() => {
    let cancelled = false;

    async function initializeMap() {
      if (!mapContainerRef.current) return;

      setIsLoading(true);
      setMapLoadError(null);

      try {
        const google = await loadGoogleMaps(resolvedApiKey);
        googleRef.current = google;
        let center = centerCoordinate ?? MOCK_CENTER;

        if (!centerCoordinate && clinicAddress) {
          try {
            center = await geocodeAddress(google, clinicAddress);
          } catch {
            center = MOCK_CENTER;
          }
        }

        if (cancelled || !mapContainerRef.current) return;

        mapCenterRef.current = center;
        const colors = getMapThemeColors();

        const map =
          mapRef.current ||
          new google.maps.Map(mapContainerRef.current, {
            center,
            zoom: getZoomForRadius(analysisRadiusMiles),
            mapTypeControl: true,
            streetViewControl: true,
            fullscreenControl: false,
            zoomControl: true,
            zoomControlOptions: { position: google.maps.ControlPosition.RIGHT_BOTTOM },
            gestureHandling: 'greedy',
            clickableIcons: true,
            isFractionalZoomEnabled: true,
            mapTypeId: 'roadmap',
          });

        mapRef.current = map;

        if (!infoWindowRef.current) {
          infoWindowRef.current = new google.maps.InfoWindow({
            disableAutoPan: false,
            pixelOffset: new google.maps.Size(0, -8),
          });
        }

        stopCameraAnimationRef.current?.();
        stopCameraAnimationRef.current = animateMapCamera(
          map,
          center,
          getZoomForRadius(analysisRadiusMiles),
        );

        if (clinicMarkerRef.current) {
          clinicMarkerRef.current.setPosition(center);
          clinicMarkerRef.current.setTitle(clinicName || MOCK_CLINIC_NAME);
        } else {
          clinicMarkerRef.current = new google.maps.Marker({
            map,
            position: center,
            title: clinicName || MOCK_CLINIC_NAME,
            icon: {
              url: `data:image/svg+xml;charset=UTF-8,${buildClinicMarkerSvg(colors)}`,
              scaledSize: new google.maps.Size(60, 60),
              anchor: new google.maps.Point(30, 30),
            },
            zIndex: 1000,
          });
        }

        if (circleRef.current) {
          circleRef.current.setCenter(center);
          circleRef.current.setRadius(analysisRadiusMiles * MILES_TO_METERS);
        } else {
          circleRef.current = new google.maps.Circle({
            map,
            center,
            radius: analysisRadiusMiles * MILES_TO_METERS,
            strokeColor: colors.success,
            strokeOpacity: 1,
            strokeWeight: 1,
            fillColor: colors.successTint,
            fillOpacity: 0.45,
          });
        }

        let fetchedCompetitors: LocalMarketCompetitor[] = [];
        if (competitorsProp?.length) {
          fetchedCompetitors = competitorsProp;
        } else {
          try {
            fetchedCompetitors = await searchNearbyClinics(
              google,
              center,
              MAX_SEARCH_RADIUS_MILES,
              clinicName || MOCK_CLINIC_NAME,
              map,
            );
          } catch {
            fetchedCompetitors = [];
          }
        }

        if (cancelled) return;

        allCompetitorsRef.current = fetchedCompetitors;
        syncVisibleCompetitors(analysisRadiusMiles);

        google.maps.event.clearListeners(map, 'click');
        map.addListener('click', () => {
          activeMarkerRef.current = null;
          infoWindowRef.current?.close();
        });

        map.panTo(center);
        setIsLoading(false);
      } catch (error) {
        if (cancelled) return;
        setIsLoading(false);
        competitorsRef.current = [];
        onCompetitorsChange?.([]);
        setMapLoadError(
          error instanceof Error
            ? error.message
            : 'Google Maps failed to load for this location.',
        );
      }
    }

    void initializeMap();

    return () => {
      cancelled = true;
      stopCameraAnimationRef.current?.();
      stopCameraAnimationRef.current = null;
    };
  }, [clinicAddress, clinicName, centerCoordinate, competitorsProp, resolvedApiKey, syncVisibleCompetitors]);

  React.useEffect(() => {
    if (!competitorsProp?.length) return;
    allCompetitorsRef.current = competitorsProp;
    syncVisibleCompetitors(analysisRadiusMiles);
  }, [analysisRadiusMiles, competitorsProp, syncVisibleCompetitors]);

  React.useEffect(() => {
    const google = googleRef.current;
    const map = mapRef.current;
    const center = mapCenterRef.current;
    if (!google || !map) return;

    stopCameraAnimationRef.current?.();
    stopCameraAnimationRef.current = animateMapCamera(
      map,
      center,
      getZoomForRadius(analysisRadiusMiles),
    );

    if (circleRef.current) {
      circleRef.current.setCenter(center);
      circleRef.current.setRadius(analysisRadiusMiles * MILES_TO_METERS);
    }

    syncVisibleCompetitors(analysisRadiusMiles);
  }, [analysisRadiusMiles, syncVisibleCompetitors]);

  React.useEffect(() => {
    function handlePointerDown(event: MouseEvent) {
      const target = event.target as HTMLElement | null;
      if (!target) {
        activeMarkerRef.current = null;
        infoWindowRef.current?.close();
        return;
      }

      const clickedMarker = target.closest('[title]');
      const clickedInfoWindow = target.closest('.gm-style-iw');
      if (clickedMarker || clickedInfoWindow) return;

      activeMarkerRef.current = null;
      infoWindowRef.current?.close();
    }

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, []);

  React.useEffect(() => {
    function handleViewMoreClick(event: MouseEvent) {
      const target = event.target as HTMLElement | null;
      const trigger = target?.closest('[data-competitor-view-more]');
      const competitorId = trigger?.getAttribute('data-competitor-view-more');
      if (!competitorId) return;

      const competitor = competitorsRef.current.find((item) => item.id === competitorId);
      if (!competitor) return;
      onCompetitorViewMore?.(competitor);
    }

    document.addEventListener('click', handleViewMoreClick);
    return () => document.removeEventListener('click', handleViewMoreClick);
  }, [onCompetitorViewMore]);

  return (
    <div className="mt-6 overflow-hidden rounded-[var(--r-xl)] border border-[var(--cream-2)] bg-[var(--cream-1)] shadow-[var(--sh-1)]">
      <div className="relative h-[560px] bg-[var(--surface-mute)]">
        <div ref={mapContainerRef} className="h-full w-full" />

        {isLoading ? (
          <div className="absolute inset-0 flex items-center justify-center bg-[color-mix(in_srgb,var(--cream-1)_70%,transparent)] backdrop-blur-[1px]">
            <div className="flex items-center gap-2 text-sm text-[var(--ink-2)]">
              <Spinner size={20} />
              <span>Loading Google map...</span>
            </div>
          </div>
        ) : null}

        {mapLoadError ? (
          <div className="absolute inset-4 flex items-center justify-center rounded-[var(--r-lg)] border border-[color-mix(in_srgb,var(--terracotta)_24%,transparent)] bg-[color-mix(in_srgb,var(--cream-1)_95%,transparent)] p-6 text-center shadow-[var(--sh-1)]">
            <div>
              <p className="text-sm font-semibold text-[var(--ink-0)]">Map unavailable</p>
              <p className="mt-2 text-sm text-[var(--ink-3)]">{mapLoadError}</p>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
